import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { money, statusLabel, statusClass, bucketClass, Pill, shortDate } from "@/lib/money";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Receipt, Ban, HandCoins, AlertTriangle } from "lucide-react";

function Stat({ label, value, tone = "" }) {
  return (
    <Card className="rounded-xl border-slate-200"><CardContent className="p-4">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"text-xl font-semibold mt-1 tabular-nums " + tone}>{value}</div>
    </CardContent></Card>
  );
}

export default function LoanDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [loan, setLoan] = useState(null);
  const [payments, setPayments] = useState([]);
  const [statement, setStatement] = useState(null);
  const [busy, setBusy] = useState(false);

  const [payOpen, setPayOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", method: "cash", reference: "", note: "" });
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [ptpOpen, setPtpOpen] = useState(false);
  const [ptp, setPtp] = useState({ promised_amount: "", promised_date: "", note: "" });
  const [woOpen, setWoOpen] = useState(false);
  const [woReason, setWoReason] = useState("");

  const load = useCallback(async () => {
    try {
      const [l, p, s] = await Promise.all([
        api.get(`/loans/${id}`),
        api.get(`/loans/${id}/payments`),
        api.get(`/loans/${id}/statement`),
      ]);
      setLoan(l.data); setPayments(p.data); setStatement(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const recordPayment = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/loans/${id}/payments`, {
        amount: Number(form.amount),
        method: form.method,
        reference: form.reference || null,
        note: form.note || null,
      });
      const p = r.data.payment;
      toast.success(
        `Receipt ${p.receipt_no} · ${money(p.amount)} applied` +
        (p.overpayment > 0 ? ` (${money(p.overpayment)} overpaid)` : "")
      );
      setPayOpen(false);
      setForm({ amount: "", method: "cash", reference: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doVoid = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/${voidTarget.id}/void`, { reason: voidReason });
      toast.success(`${voidTarget.receipt_no} voided`);
      setVoidTarget(null); setVoidReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doPtp = async () => {
    setBusy(true);
    try {
      await api.post(`/loans/${id}/promise-to-pay`, {
        promised_amount: Number(ptp.promised_amount),
        promised_date: ptp.promised_date,
        note: ptp.note || null,
      });
      toast.success("Payment arrangement recorded");
      setPtpOpen(false); setPtp({ promised_amount: "", promised_date: "", note: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doWriteOff = async () => {
    setBusy(true);
    try {
      await api.post(`/loans/${id}/write-off`, { reason: woReason });
      toast.success("Loan written off");
      setWoOpen(false); setWoReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!loan) {
    return <Layout><div className="p-8 text-slate-500">Loading loan…</div></Layout>;
  }

  const canAct = loan.status === "active";

  return (
    <Layout>
      <div className="fade-in space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <Link to="/admin/loans" className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" /> Loan book
            </Link>
            <h1 className="text-2xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Outfit" }}>
              {loan.product_name}
            </h1>
            <p className="text-sm text-slate-500">
              {loan.borrower_email} · opened {shortDate(loan.opened_at)} ·{" "}
              <Pill className={statusClass(loan.status)}>{statusLabel(loan.status)}</Pill>
              {loan.status === "active" && <> <Pill className={bucketClass(loan.bucket)}>{loan.bucket}</Pill></>}
            </p>
          </div>
          {canAct && (
            <div className="flex gap-2 flex-wrap">
              <Button onClick={() => setPayOpen(true)} className="bg-blue-900 hover:bg-blue-800" data-testid="record-payment-btn">
                <Receipt className="h-4 w-4 mr-1" /> Record payment
              </Button>
              <Button variant="outline" onClick={() => setPtpOpen(true)} data-testid="ptp-btn">
                <HandCoins className="h-4 w-4 mr-1" /> Promise to pay
              </Button>
              {isAdmin && (
                <Button variant="outline" className="text-red-700 border-red-200 hover:bg-red-50"
                        onClick={() => setWoOpen(true)} data-testid="writeoff-btn">
                  <AlertTriangle className="h-4 w-4 mr-1" /> Write off
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Outstanding" value={money(loan.outstanding)} />
          <Stat label="Paid to date" value={money(loan.total_paid)} />
          <Stat label="Arrears" value={loan.arrears > 0 ? money(loan.arrears) : money(0)}
                tone={loan.arrears > 0 ? "text-red-700" : ""} />
          <Stat label="Days past due" value={loan.days_past_due > 0 ? String(loan.days_past_due) : "—"}
                tone={loan.days_past_due > 0 ? "text-red-700" : ""} />
        </div>

        <Tabs defaultValue="schedule">
          <TabsList>
            <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments ({payments.length})</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-ledger">Statement</TabsTrigger>
          </TabsList>

          <TabsContent value="schedule">
            <Card className="rounded-xl border-slate-200"><CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2">#</th>
                      <th className="text-left px-4 py-2">Due</th>
                      <th className="text-right px-4 py-2">Amount</th>
                      <th className="text-right px-4 py-2">Paid</th>
                      <th className="text-right px-4 py-2">Balance</th>
                      <th className="text-left px-4 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loan.schedule.map((i) => (
                      <tr key={i.installment} className="border-t border-slate-100">
                        <td className="px-4 py-2 text-slate-500">{i.installment}</td>
                        <td className="px-4 py-2 whitespace-nowrap">{shortDate(i.due_date)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{money(i.amount)}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-slate-600">{money(i.paid)}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">{money(i.balance)}</td>
                        <td className="px-4 py-2"><Pill className={statusClass(i.status)}>{statusLabel(i.status)}</Pill></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card className="rounded-xl border-slate-200"><CardContent className="p-0">
              {payments.length === 0 ? (
                <div className="p-10 text-center">
                  <p className="font-medium">No payments recorded yet</p>
                  <p className="text-sm text-slate-500 mt-1">Use “Record payment” when funds are received.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                      <tr>
                        <th className="text-left px-4 py-2">Receipt</th>
                        <th className="text-left px-4 py-2">Received</th>
                        <th className="text-right px-4 py-2">Amount</th>
                        <th className="text-left px-4 py-2">Allocation</th>
                        <th className="text-left px-4 py-2">Status</th>
                        {isAdmin && <th className="px-4 py-2"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p.id} className="border-t border-slate-100" data-testid={`payment-${p.receipt_no}`}>
                          <td className="px-4 py-2 font-mono text-xs">{p.receipt_no}</td>
                          <td className="px-4 py-2 whitespace-nowrap">{shortDate(p.received_at)}</td>
                          <td className="px-4 py-2 text-right tabular-nums font-medium">{money(p.amount)}</td>
                          <td className="px-4 py-2 text-xs text-slate-600">
                            {["penalties", "fees", "interest", "principal"]
                              .filter((k) => (p.allocation || {})[k] > 0)
                              .map((k) => `${k} ${money(p.allocation[k])}`)
                              .join(" · ") || "—"}
                            {p.overpayment > 0 && <span className="text-amber-700"> · overpaid {money(p.overpayment)}</span>}
                          </td>
                          <td className="px-4 py-2">
                            <Pill className={statusClass(p.status)}>{statusLabel(p.status)}</Pill>
                            {p.status === "voided" && p.void_reason && (
                              <div className="text-[11px] text-slate-500 mt-0.5">{p.void_reason}</div>
                            )}
                          </td>
                          {isAdmin && (
                            <td className="px-4 py-2 text-right">
                              {p.status === "posted" && (
                                <Button variant="outline" size="sm" className="h-8 px-2 text-xs text-red-700 border-red-200 hover:bg-red-50"
                                        onClick={() => setVoidTarget(p)} data-testid={`void-${p.receipt_no}`}>
                                  <Ban className="h-3.5 w-3.5 mr-1" /> Void
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="ledger">
            <Card className="rounded-xl border-slate-200"><CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2">Date</th>
                      <th className="text-left px-4 py-2">Entry</th>
                      <th className="text-right px-4 py-2">Amount</th>
                      <th className="text-right px-4 py-2">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(statement?.entries || []).map((e) => (
                      <tr key={e.id} className="border-t border-slate-100">
                        <td className="px-4 py-2 whitespace-nowrap">{shortDate(e.entry_date)}</td>
                        <td className="px-4 py-2">{e.memo}</td>
                        <td className={"px-4 py-2 text-right tabular-nums " + (e.amount < 0 ? "text-emerald-700" : "")}>
                          {money(e.amount)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">{money(e.balance_after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {statement?.reconciliation && (
                <div className={"px-4 py-3 text-sm border-t " +
                  (statement.reconciliation.matches_loan_outstanding
                    ? "bg-emerald-50 text-emerald-800 border-emerald-100"
                    : "bg-red-50 text-red-800 border-red-100")}>
                  {statement.reconciliation.matches_loan_outstanding
                    ? `Reconciled — charged ${money(statement.reconciliation.total_charged)} less credited ${money(statement.reconciliation.total_credited)} equals the balance of ${money(statement.reconciliation.closing_balance)}.`
                    : `DOES NOT RECONCILE — statement closes at ${money(statement.reconciliation.closing_balance)} but the loan reports ${money(loan.outstanding)}.`}
                </div>
              )}
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* record payment */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record a payment</DialogTitle>
            <DialogDescription>
              Applied in order: penalties, then fees, then interest, then principal.
              Outstanding is currently {money(loan.outstanding)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount received</Label>
              <Input type="number" step="0.01" min="0.01" value={form.amount} autoFocus
                     onChange={(e) => setForm({ ...form, amount: e.target.value })}
                     data-testid="payment-amount" />
            </div>
            <div>
              <Label>Method</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="e_wallet">E-wallet</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="payroll_deduction">Payroll deduction</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Reference</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                     placeholder="Bank reference or receipt book no." data-testid="payment-reference" /></div>
            <div><Label>Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button onClick={recordPayment} disabled={busy || !Number(form.amount)}
                    className="bg-blue-900 hover:bg-blue-800" data-testid="save-payment-btn">
              Record {form.amount ? money(Number(form.amount)) : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* void */}
      <Dialog open={!!voidTarget} onOpenChange={(o) => { if (!o) { setVoidTarget(null); setVoidReason(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void {voidTarget?.receipt_no}?</DialogTitle>
            <DialogDescription>
              This reverses {money(voidTarget?.amount)} from the loan. The receipt is kept and the
              reversal is posted to the ledger — nothing is erased.
            </DialogDescription>
          </DialogHeader>
          <div><Label>Reason</Label>
            <Input value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                   placeholder="Why is this being reversed?" data-testid="void-reason" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidTarget(null)}>Cancel</Button>
            <Button onClick={doVoid} disabled={busy || voidReason.trim().length < 3}
                    className="bg-red-700 hover:bg-red-800" data-testid="confirm-void-btn">Void payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* promise to pay */}
      <Dialog open={ptpOpen} onOpenChange={setPtpOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record a payment arrangement</DialogTitle>
            <DialogDescription>What has the borrower committed to, and by when?</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Promised amount</Label>
              <Input type="number" step="0.01" value={ptp.promised_amount}
                     onChange={(e) => setPtp({ ...ptp, promised_amount: e.target.value })}
                     data-testid="ptp-amount" /></div>
            <div><Label>Promised date</Label>
              <Input type="date" value={ptp.promised_date}
                     onChange={(e) => setPtp({ ...ptp, promised_date: e.target.value })}
                     data-testid="ptp-date" /></div>
            <div><Label>Note</Label>
              <Input value={ptp.note} onChange={(e) => setPtp({ ...ptp, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPtpOpen(false)}>Cancel</Button>
            <Button onClick={doPtp} disabled={busy || !Number(ptp.promised_amount) || !ptp.promised_date}
                    className="bg-blue-900 hover:bg-blue-800" data-testid="save-ptp-btn">Record arrangement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* write-off */}
      <AlertDialog open={woOpen} onOpenChange={setWoOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Write off this loan?</AlertDialogTitle>
            <AlertDialogDescription>
              {money(loan.outstanding)} will be written off and the loan will leave the active
              portfolio. The debt is not forgiven in law — this is an accounting action, it is
              audited, and no further payments can be recorded against the loan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1"><Label>Reason</Label>
            <Input value={woReason} onChange={(e) => setWoReason(e.target.value)}
                   placeholder="Basis for the write-off" data-testid="writeoff-reason" /></div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doWriteOff} disabled={busy || woReason.trim().length < 3}
                               data-testid="confirm-writeoff-btn">Write off</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
